import {FilesetResolver, ImageSegmenter} from '@mediapipe/tasks-vision'
import type {
  BackgroundOptions,
  TrackTransformerDestroyOptions,
  VideoTransformerInitOptions,
} from '@livekit/track-processors'
import {
  defaultStabilizedMaskTuning,
  setupStabilizedBackgroundGL,
  type IStabilizedBackgroundGL,
  type IStabilizedMaskTuning,
} from '@toolify/client/src/modules/call/processors/setupStabilizedBackgroundGL'

import {dependencies} from '@toolify/client/package.json'

const TASKS_VISION_VERSION = dependencies['@mediapipe/tasks-vision']
const DEFAULT_TASKS_VISION_FILE_SET = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}/wasm`
const DEFAULT_MODEL_ASSET_PATH = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite'

export type StabilizedBackgroundOptions = BackgroundOptions & {
  maskTuning?: Partial<IStabilizedMaskTuning>
}

interface IPersonMaskSource {
  index: number
  invert: boolean
}

let webgl2Supported: boolean | undefined

function detectWebgl2(): boolean {
  if (webgl2Supported === undefined) {
    const context = document.createElement('canvas').getContext('webgl2')
    webgl2Supported = !!context
    context?.getExtension('WEBGL_lose_context')?.loseContext()
  }
  return webgl2Supported
}

function resolvePersonMaskSource(labels: string[]): IPersonMaskSource {
  const personIndex = labels.findIndex((label) => /person|selfie|foreground/i.test(label))
  if (personIndex >= 0) {
    return {
      index: personIndex,
      invert: false,
    }
  }
  const backgroundIndex = labels.findIndex((label) => /background/i.test(label))
  if (backgroundIndex >= 0 && labels.length === 2) {
    return {
      index: 1 - backgroundIndex,
      invert: false,
    }
  }
  if (backgroundIndex >= 0) {
    return {
      index: backgroundIndex,
      invert: true,
    }
  }
  return {
    index: Math.max(0, labels.length - 1),
    invert: false,
  }
}

export class StabilizedBackgroundTransformer {
  transformer?: TransformStream
  options: StabilizedBackgroundOptions
  segmentationTimeMs = 0
  isFirstFrame = true

  protected isDisabled = false
  protected canvas?: OffscreenCanvas | HTMLCanvasElement
  protected inputVideo?: HTMLVideoElement
  protected gl?: IStabilizedBackgroundGL
  protected imageSegmenter?: ImageSegmenter
  protected personMaskSource: IPersonMaskSource = {
    index: 0,
    invert: false,
  }
  protected backgroundImageAndPath: {imageData: ImageBitmap; path: string} | null = null
  protected initGeneration = 0

  constructor(opts: StabilizedBackgroundOptions) {
    this.options = opts
  }

  static get isSupported() {
    return (
      typeof OffscreenCanvas !== 'undefined'
      && typeof VideoFrame !== 'undefined'
      && typeof createImageBitmap !== 'undefined'
      && detectWebgl2()
    )
  }

  async init({outputCanvas, inputElement: inputVideo}: VideoTransformerInitOptions) {
    if (!(inputVideo instanceof HTMLVideoElement)) {
      throw TypeError('Video transformer needs a HTMLVideoElement as input')
    }
    const generation = ++this.initGeneration
    this.transformer = new TransformStream({
      transform: (frame, controller) => this.transform(frame, controller),
    })
    this.canvas = outputCanvas
    this.gl = setupStabilizedBackgroundGL(outputCanvas, {
      ...defaultStabilizedMaskTuning,
      ...this.options.maskTuning,
    })
    this.inputVideo = inputVideo
    this.isDisabled = false

    const fileSet = await FilesetResolver.forVisionTasks(
      this.options.assetPaths?.tasksVisionFileSet ?? DEFAULT_TASKS_VISION_FILE_SET,
    )
    if (generation !== this.initGeneration) {
      return
    }
    const imageSegmenter = await ImageSegmenter.createFromOptions(fileSet, {
      baseOptions: {
        modelAssetPath: this.options.assetPaths?.modelAssetPath ?? DEFAULT_MODEL_ASSET_PATH,
        delegate: 'GPU',
        ...this.options.segmenterOptions,
      },
      canvas: this.canvas,
      runningMode: 'VIDEO',
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    })
    if (generation !== this.initGeneration || this.isDisabled) {
      await imageSegmenter.close()
      return
    }
    this.imageSegmenter = imageSegmenter
    this.personMaskSource = resolvePersonMaskSource(this.imageSegmenter.getLabels())
    this.gl?.setMaskInvert(this.personMaskSource.invert)

    if (this.options.imagePath) {
      await this.loadAndSetBackground(this.options.imagePath).catch(() => undefined)
    }
    if (typeof this.options.blurRadius === 'number') {
      this.gl?.setBlurRadius(this.options.blurRadius)
    }
    this.gl?.setBackgroundDisabled(this.options.backgroundDisabled ?? false)
  }

  async restart(options: VideoTransformerInitOptions) {
    await this.destroy({willProcessorRestart: true})
    await this.init(options)
  }

  async destroy(options?: TrackTransformerDestroyOptions) {
    this.initGeneration++
    this.isDisabled = true
    this.canvas = undefined
    this.gl?.cleanup()
    this.gl = undefined
    await this.imageSegmenter?.close()
    this.imageSegmenter = undefined
    this.backgroundImageAndPath = null
    if (!options?.willProcessorRestart) {
      this.isFirstFrame = true
    }
  }

  async loadAndSetBackground(path: string) {
    if (!this.backgroundImageAndPath || this.backgroundImageAndPath.path !== path) {
      const img = new Image()
      await new Promise((resolve, reject) => {
        img.crossOrigin = 'Anonymous'
        img.onload = () => resolve(img)
        img.onerror = (err) => reject(err)
        img.src = path
      })
      const imageData = await createImageBitmap(img)
      this.backgroundImageAndPath = {
        imageData,
        path,
      }
    }
    await this.gl?.setBackgroundImage(this.backgroundImageAndPath.imageData)
  }

  async transform(frame: VideoFrame, controller: TransformStreamDefaultController<VideoFrame>) {
    let enqueuedFrame = false
    try {
      if (!(frame instanceof VideoFrame) || frame.codedWidth === 0 || frame.codedHeight === 0) {
        return
      }
      let skipProcessingFrame = this.isDisabled || (this.options.backgroundDisabled ?? false)
      if (typeof this.options.blurRadius !== 'number' && typeof this.options.imagePath !== 'string') {
        skipProcessingFrame = true
      }
      if (skipProcessingFrame) {
        controller.enqueue(frame)
        enqueuedFrame = true
        return
      }
      const frameTimeMs = Date.now()
      if (!this.canvas) {
        throw TypeError('Canvas needs to be initialized first')
      }
      this.canvas.width = frame.displayWidth
      this.canvas.height = frame.displayHeight
      if (this.isFirstFrame) {
        controller.enqueue(frame.clone())
        const inputVideo = this.inputVideo
        if (inputVideo) {
          await new Promise<void>((resolve) => {
            inputVideo.requestVideoFrameCallback((_now, metadata) => {
              const durationUntilFrameRenderedInMs = metadata.expectedDisplayTime - metadata.presentationTime
              setTimeout(resolve, durationUntilFrameRenderedInMs)
            })
          })
        }
      }
      this.isFirstFrame = false
      const filterStartTimeMs = performance.now()
      const segmentationPromise = new Promise<void>((resolve, reject) => {
        try {
          const segmentationStartTimeMs = performance.now()
          this.imageSegmenter?.segmentForVideo(frame, segmentationStartTimeMs, (result) => {
            this.segmentationTimeMs = performance.now() - segmentationStartTimeMs
            const masks = result.confidenceMasks
            if (masks && masks.length > 0) {
              const maskIndex = Math.min(this.personMaskSource.index, masks.length - 1)
              this.gl?.updateMask(masks[maskIndex].getAsWebGLTexture())
            }
            result.close()
            resolve()
          })
        } catch (error) {
          reject(error)
        }
      })
      this.gl?.renderFrame(frame)
      if (this.canvas && this.canvas.width > 0 && this.canvas.height > 0) {
        const newFrame = new VideoFrame(this.canvas, {
          timestamp: frame.timestamp || frameTimeMs,
        })
        controller.enqueue(newFrame)
        const filterTimeMs = performance.now() - filterStartTimeMs
        this.options.onFrameProcessed?.({
          processingTimeMs: this.segmentationTimeMs + filterTimeMs,
          segmentationTimeMs: this.segmentationTimeMs,
          filterTimeMs,
        })
      } else {
        controller.enqueue(frame)
        enqueuedFrame = true
      }
      await segmentationPromise
    } catch (_error) {
      void _error
    } finally {
      if (!enqueuedFrame) {
        frame.close()
      }
    }
  }

  async update(opts: StabilizedBackgroundOptions) {
    this.options = {
      ...this.options,
      ...opts,
    }
    this.gl?.setBlurRadius(this.options.blurRadius ?? null)
    if (this.options.imagePath) {
      await this.loadAndSetBackground(this.options.imagePath)
    } else {
      await this.gl?.setBackgroundImage(null)
    }
    this.gl?.setBackgroundDisabled(this.options.backgroundDisabled ?? false)
  }
}
